import React, {useEffect, useState} from 'react';
import {browserHistory} from 'react-router';
import {List, Map, Set} from 'immutable';

import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';

import DialogContentText from '@material-ui/core/DialogContentText';

import {KpiAssignment, KpiOrderBy, KpiTargetId, KpiTargetTree} from '@store/@types/kpi';
import KpiGoalActions from '@store/kpi/KpiTargetActions';
import KpiGoalStore from '@store/kpi/KpiTargetStore';

import {ChainLinkButton} from './ChainLinkButton';
import GoalKpiRow from './kpi-goal-row';

import './KpiGoalsList.styl';
import User, {UserId} from 'records/User';
import UserActions from 'actions/UserActions';
import {KpiHeader} from '../assignment-list/KpiHeader';

enum HEADER {
  NAME = 'name',
  USER_ID = 'createdByUserId',
  FREQUENCY = 'frequency',
  CONTINUITY = 'continuity',
}

export interface KpiGoalsListProps {
  fetchGoalKpiTree: typeof KpiGoalActions.fetchGoalKpiTree;
  kpiTargetTrees: List<KpiTargetTree>;
  selectedGoal: KpiTargetId | undefined;
  selectedKpis: Set<KpiTargetId>;
  users: Map<UserId, User>;
  fetchUsers: typeof UserActions.fetchAll;
  assignmentsByKpiId: Map<KpiTargetId, List<KpiAssignment>>;
  direction: 'asc' | 'desc';
  orderBy: KpiOrderBy | undefined;
  setOrderBy: (o: KpiOrderBy) => void;
}

export const KpiGoalsList: React.FC<KpiGoalsListProps> = ({
  fetchGoalKpiTree,
  kpiTargetTrees,
  selectedGoal,
  selectedKpis,
  users,
  fetchUsers,
  assignmentsByKpiId,
  direction,
  orderBy,
  setOrderBy,
}) => {
  useEffect(() => {
    fetchGoalKpiTree().then().catch(console.error);
    fetchUsers({}).then().catch(console.error);
  }, [fetchGoalKpiTree, fetchUsers]);

  const [childWarning, setChildWarning] = useState<boolean>(false);

  if (!kpiTargetTrees || !users || !selectedKpis) {
    return null;
  }

  /**
   * Handles navigation for association depending on the current selections
   * Also checks if any of the selected kpis are children, which must be unassociated
   * before continuing
   */
  const associateKpis = () => {
    const topLevelIds = kpiTargetTrees.map(t => t.kpiTarget.id).toSet();

    // Check the selected kpis to see if theyre being used already.
    // Kpis linked to a goal won't show up in this set.
    if (selectedKpis.every(id => topLevelIds.has(id))) {
      if (selectedGoal) {
        // User has a goal selected, navigate to the edit page for this goal
        browserHistory.push(`/admin/kpis/edit/${selectedGoal}`);
      }

      // Otherwise, navigate to the create goal page
      browserHistory.push(`/admin/kpis/create`, {state: {goal: true}});
    } else {
      // Warn the user that they must first disassociate the KPIs from their parent goals.
      setChildWarning(true);
    }
  };

  const onClose = () => setChildWarning(false);

  const getSortedTrees = () => {
    switch (orderBy) {
      case HEADER.USER_ID:
        return kpiTargetTrees.sortBy(t => users.get(t.kpiTarget.createdByUserId)?.getDisplayName());
      case HEADER.CONTINUITY:
        return kpiTargetTrees.sortBy(t => t.kpiTarget.continuity);
      case HEADER.FREQUENCY:
        return kpiTargetTrees.sortBy(t => t.kpiTarget.frequency);
      default:
        return kpiTargetTrees.sortBy(t => t.kpiTarget.name);
    }
  };
  const sortedTrees = direction === 'asc' ? getSortedTrees() : getSortedTrees().reverse();

  return (
    <>
      <ChainLinkButton
        kpisCount={selectedKpis.size}
        disabled={selectedKpis.size < 2}
        onClick={associateKpis}
      />
      <table className="kpi-goals-list">
        <thead>
          <tr>
            <KpiHeader
              className="goal-kpi"
              name={HEADER.NAME}
              setOrder={setOrderBy}
              orderBy={orderBy}
              direction={direction}>
              Goal / KPI
            </KpiHeader>
            <KpiHeader
              className="owner"
              name={HEADER.USER_ID}
              setOrder={setOrderBy}
              orderBy={orderBy}
              direction={direction}>
              Owner
            </KpiHeader>
            <KpiHeader
              className="frequency"
              name={HEADER.FREQUENCY}
              setOrder={setOrderBy}
              orderBy={orderBy}
              direction={direction}>
              Frequency
            </KpiHeader>
            <KpiHeader
              className="continuity"
              name={HEADER.CONTINUITY}
              setOrder={setOrderBy}
              orderBy={orderBy}
              direction={direction}>
              Continuity
            </KpiHeader>
            <th className="cog" />
          </tr>
        </thead>
        <tbody>
          {sortedTrees.map(tree => (
            <GoalKpiRow
              key={tree.kpiTarget.id}
              kpiTarget={tree.kpiTarget}
              kpiChildren={tree.kpiChildren}
              selectedKpis={selectedKpis}
              selectedGoal={selectedGoal}
              users={users}
              assignmentsByKpiId={assignmentsByKpiId}
            />
          ))}
        </tbody>
      </table>
      <Dialog open={childWarning} onClose={onClose}>
        <DialogTitle>KPI Already In Use</DialogTitle>
        <DialogContent>
          <DialogContentText>
            One or more of the KPIs you're attempting to merge are already being used by a Goal.
            KPIs can only be associated with one Goal at a time. Do you wish to disassociate these
            KPIs?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button onClick={() => disassociateKpis().then(onClose)} color="primary">
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

/**
 * Disassociate the selected KPIs from their parents so they can be re-associated later.
 * TODO: better move to @store folder
 */
async function disassociateKpis() {
  const {selectedKpis, kpiTargetTree} = KpiGoalStore.getState();
  const treesToUpdate = kpiTargetTree
    // Find the trees we need to update
    .filter(t => t.kpiChildren.some(c => selectedKpis.has(c.id)))
    // Abduct their children
    .map(
      t =>
        ({
          kpiTarget: t.kpiTarget,
          kpiChildren: t.kpiChildren.filter(c => !selectedKpis.has(c.id)),
        } as KpiTargetTree)
    )
    // Then map to request payload
    .map(({kpiTarget: kpi, kpiChildren}) => ({
      id: kpi.id,
      name: kpi.name,
      numericType: kpi.numericType,
      value: kpi.value,
      frequency: kpi.frequency,
      continuity: kpi.continuity,
      childIds: kpiChildren.map(c => c.id).toJS(),
      skillIds: kpi.skillIds.toJS(),
      chartAsSkill: kpi.chartAsSkill,
      chartAsEngagement: kpi.chartAsEngagement,
    }));

  await Promise.all(treesToUpdate.map(({id, ...params}) => KpiGoalActions.update(id, params)));
  return KpiGoalActions.fetchGoalKpiTree();
}
